"use client"

import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { MdSportsCricket, MdSportsScore } from "react-icons/md"
import {
  FiCalendar,
  FiMapPin,
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiTarget,
  FiEdit2,
} from "react-icons/fi"
import api from "../../libs/axios"
import Breadcrumb from "../../utils/breadcrumbs"
import formatDate from "../../utils/format-date"
import { DetailsLoader } from "../../utils/loader"

interface Player {
  id: number
  player_name: string
  player_role: number
  jersey_number?: number
}

interface Team {
  id: number
  name: string
  logo_url?: string | null
  location?: string
}

interface LineupPlayer {
  id: number
  match_id: number
  team_id: number
  team_player_id: number
  batting_order?: number | null
  is_playing_eleven: boolean
  is_captain: boolean
  is_wicket_keeper: boolean
  is_substitute: boolean
  player: Player
  team: Team
}

interface Innings {
  id: number
  match_id: number
  batting_team_id: number
  bowling_team_id: number
  innings_number: number
  total_runs: number
  total_wickets: number
  total_overs: number
  total_balls: number
  extras_byes: number
  extras_leg_byes: number
  extras_wides: number
  extras_no_balls: number
  extras_penalty: number
  is_completed: boolean
  completed_at?: string | null
}

interface Ground {
  id: string
  name: string
  value: string
  description: string
}

interface MatchData {
  no_ball_runs: string
  wide_runs: string
  id: number
  title: string
  match_type: string
  tournament_id?: number | null
  organizer_id: number
  team_a_id: number
  team_b_id: number
  match_date: string
  ground_id: string
  ground_latitude: string
  ground_longitude: string
  ball_type: string
  pitch_type: string
  total_overs: number
  max_overs_per_bowler: number
  powerplay_overs: number
  powerplay_enabled: boolean
  free_hit_enabled: boolean
  super_over_enabled: boolean
  city: string
  setup_step: string
  toss_winner_team_id?: number | null
  toss_decision?: string | null
  status: string
  match_status: string
  winning_team_id?: number | null
  man_of_match_id?: number | null
  result_summary?: string | null
  current_striker_id?: number | null
  current_non_striker_id?: number | null
  current_bowler_id?: number | null
  current_bowler_style?: string | null
  created_at: string
  updated_at: string
  teamA: Team
  teamB: Team
  tossWinner?: Team | null
  winner?: Team | null
  organizer: {
    id: number
    phone_number: string
    details?: any
  }
  currentStriker?: Player | null
  currentNonStriker?: Player | null
  currentBowler?: Player | null
  lineup: LineupPlayer[]
  ground: Ground
  innings: Innings[]
}

const StatusBadge = ({ status }: { status: string }) => {
  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case "live":
      case "active":
        return { color: "bg-red-600 text-white", text: "LIVE" }
      case "completed":
        return { color: "bg-green-600 text-white", text: "COMPLETED" }
      case "scheduled":
        return { color: "bg-primary-600 text-white", text: "SCHEDULED" }
      case "cancelled":
        return { color: "bg-gray-600 text-white", text: "CANCELLED" }
      default:
        return { color: "bg-yellow-600 text-white", text: status.toUpperCase() }
    }
  }

  const config = getStatusConfig(status)

  return (
    <span className={`inline-flex items-center px-4 py-1 rounded-lg text-sm font-bold ${config.color}`}>
      {config.text}
    </span>
  )
}

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "blue",
}: {
  title: string
  value: string
  subtitle?: string
  icon: any
  color?: string
}) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-primary-600",
    green: "bg-green-50 border-green-200 text-green-600",
    red: "bg-red-50 border-red-200 text-red-600",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-600",
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color as keyof typeof colorClasses]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
        <p className="text-gray-600 font-medium">{title}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  )
}

export default function MatchDetails() {
  const location = useLocation()
  const matchId = location.state?.matchId
  const [match, setMatch] = useState<MatchData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState("overview")

  const fetchMatch = async () => {
    if (!matchId) {
      setError("No match ID provided")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await api.get(`/matches/${matchId}/info`)
      setMatch(response.data.match)
      setError(null)
    } catch (err) {
      console.error("Error fetching match:", err)
      setError("Failed to load match data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMatch()
  }, [matchId])

  if (loading) {
    return <DetailsLoader />
  }

  if (error || !match) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <MdSportsCricket className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Match</h2>
          <p className="text-gray-600 mb-4">{error || "Match data not available"}</p>
          <button onClick={fetchMatch} className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const getTossInfo = () => {
    if (!match.tossWinner || !match.toss_decision) return <span className="font-bold">Toss not completed</span>
    return (
      <span>
        <span className="font-bold capitalize">{match.tossWinner.name}</span> won the toss and chose to{" "}
        <span className="font-bold capitalize">{match.toss_decision}</span> first
      </span>
    )
  }

  const getCurrentInnings = () => {
    if (!match.innings || match.innings.length === 0) return null
    return match.innings.reduce((latest, current) =>
      current.innings_number > latest.innings_number ? current : latest
    )
  }

  const getInningsByTeam = (teamId: number) => {
    return match.innings?.find((inn) => inn.batting_team_id === teamId) || null
  }

  const getTeamPlayers = (teamId: number) => {
    return match.lineup?.filter((player) => player.team_id === teamId && player.is_playing_eleven) || []
  }

  const currentInnings = getCurrentInnings()
  const teamAPlayers = getTeamPlayers(match.team_a_id)
  const teamBPlayers = getTeamPlayers(match.team_b_id)
  const teamAInnings = getInningsByTeam(match.team_a_id)
  const teamBInnings = getInningsByTeam(match.team_b_id)

  return (
    <div className="w-full min-h-screen bg-white p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-xl font-bold text-primary-950 flex items-center">
          <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
          View Match
        </h2>
        <Breadcrumb />
      </div>

      <div className="bg-white border-b border-gray-200">
        <div className="mx-auto py-6 border-t">
          <div className="flex gap-2 justify-end">
            <Link
              to="/matches/edit"
              state={{ matchId: match.id }}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <FiEdit2 className="w-4 h-4" />
              Edit
            </Link>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm">
              Delete
            </button>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="bg-blue-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
                  {match.match_type?.toUpperCase() || "MATCH"}
                </span>
                <StatusBadge status={match.status} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{match.title}</h2>
              <div className="md:flex items-center space-y-1 gap-6 text-gray-600">
                <div className="flex items-center gap-2">
                  <FiCalendar className="w-4 h-4" />
                  <span>{formatDate(match.match_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiMapPin className="w-4 h-4" />
                  <span>{match.ground?.name || match.city}</span>
                </div>
                <div className="flex items-center gap-2">
                  {!match.toss_decision ? (
                    <FiClock className="w-4 h-4" />
                  ) : (
                    <FiCheckCircle className="w-4 h-4 text-green-600" />
                  )}
                  <span>{getTossInfo()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{match.teamA?.name?.charAt(0) || "A"}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{match.teamA?.name}</h3>
                    <p className="text-gray-600">{match.teamA?.location}</p>
                  </div>
                </div>
              </div>
              {teamAInnings && (
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">
                    {teamAInnings.total_runs}/{teamAInnings.total_wickets}
                  </div>
                  <div className="text-gray-600">{teamAInnings.total_overs} Overs</div>
                </div>
              )}
            </div>

            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{match.teamB?.name?.charAt(0) || "B"}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{match.teamB?.name}</h3>
                    <p className="text-gray-600">{match.teamB?.location}</p>
                  </div>
                </div>
              </div>
              {teamBInnings && (
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">
                    {teamBInnings.total_runs}/{teamBInnings.total_wickets}
                  </div>
                  <div className="text-gray-600">{teamBInnings.total_overs} Overs</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {match.status === "active" && (match.currentStriker || match.currentNonStriker || match.currentBowler) && (
        <div className="bg-white border-b border-gray-200">
          <div className="mx-auto py-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Current Players</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {match.currentStriker && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{match.currentStriker.player_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{match.currentStriker.player_name}</h4>
                      <p className="text-sm text-gray-600">Striker</p>
                    </div>
                  </div>
                </div>
              )}

              {match.currentNonStriker && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{match.currentNonStriker.player_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{match.currentNonStriker.player_name}</h4>
                      <p className="text-sm text-gray-600">Non-Striker</p>
                    </div>
                  </div>
                </div>
              )}

              {match.currentBowler && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{match.currentBowler.player_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{match.currentBowler.player_name}</h4>
                      <p className="text-sm text-gray-600">Bowler</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Overs"
            value={match.total_overs?.toString() || "0"}
            subtitle={`Max ${match.max_overs_per_bowler || 0} per bowler`}
            icon={FiClock}
            color="blue"
          />
          <StatCard
            title="Ball Type"
            value={match.ball_type?.charAt(0).toUpperCase() + match.ball_type?.slice(1) || "N/A"}
            subtitle={`${match.pitch_type?.charAt(0).toUpperCase() + match.pitch_type?.slice(1)} pitch`}
            icon={MdSportsScore}
            color="green"
          />
          <StatCard
            title="Players"
            value={match.lineup?.filter((p) => p.is_playing_eleven).length.toString() || "0"}
            subtitle="Total Players"
            icon={FiUsers}
            color="yellow"
          />
          {currentInnings && (
            <StatCard
              title="Current Score"
              value={`${currentInnings.total_runs}/${currentInnings.total_wickets}`}
              subtitle={`${currentInnings.total_overs} overs`}
              icon={FiTarget}
              color="red"
            />
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-4 md:px-6">
              {["overview", "scorecard", "squads"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 md:p-6">
            {activeTab === "overview" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Match Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 border">
                      <h4 className="font-medium text-gray-900 mb-2">Match Details</h4>
                      <div className="capitalize space-y-2 text-sm text-gray-600">
                        <p>
                          <span className="font-medium">Type:</span> {match.match_type}
                        </p>
                        <p>
                          <span className="font-medium">City:</span> {match.city}
                        </p>
                        <p>
                          <span className="font-medium">Ground:</span> {match.ground?.name}
                        </p>
                        <p>
                          <span className="font-medium">Ball:</span> {match.ball_type}
                        </p>
                        <p>
                          <span className="font-medium">Pitch:</span> {match.pitch_type}
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border">
                      <h4 className="font-medium text-gray-900 mb-2">Match Settings</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p>
                          <span className="font-medium">Powerplay:</span>{" "}
                          {match.powerplay_enabled ? `${match.powerplay_overs} overs` : "Disabled"}
                        </p>
                        <p>
                          <span className="font-medium">Free Hit:</span> {match.free_hit_enabled ? "Enabled" : "Disabled"}
                        </p>
                        <p>
                          <span className="font-medium">Super Over:</span>{" "}
                          {match.super_over_enabled ? "Enabled" : "Disabled"}
                        </p>
                        <p>
                          <span className="font-medium">Wide Runs:</span> {match.wide_runs}
                        </p>
                        <p>
                          <span className="font-medium">No Ball Runs:</span> {match.no_ball_runs}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "scorecard" && (
              <div className="space-y-6">
                {match.innings && match.innings.length > 0 ? (
                  match.innings
                    .sort((a, b) => a.innings_number - b.innings_number)
                    .map((innings) => {
                      const battingTeam = innings.batting_team_id === match.team_a_id ? match.teamA : match.teamB
                      return (
                        <div key={innings.id}>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {battingTeam?.name} Innings {innings.innings_number}
                          </h3>
                          <div className="bg-gray-50 rounded-lg p-4 border">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                              <div>
                                <div className="text-2xl font-bold text-gray-900">{innings.total_runs}</div>
                                <div className="text-sm text-gray-600">Runs</div>
                              </div>
                              <div>
                                <div className="text-2xl font-bold text-gray-900">{innings.total_wickets}</div>
                                <div className="text-sm text-gray-600">Wickets</div>
                              </div>
                              <div>
                                <div className="text-2xl font-bold text-gray-900">{innings.total_overs}</div>
                                <div className="text-sm text-gray-600">Overs</div>
                              </div>
                              <div>
                                <div className="text-2xl font-bold text-gray-900">
                                  {innings.total_overs > 0 ? (innings.total_runs / innings.total_overs).toFixed(2) : "0.00"}
                                </div>
                                <div className="text-sm text-gray-600">Run Rate</div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <h4 className="font-medium text-gray-900 mb-3">Extras</h4>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                              <div className="bg-white rounded-lg p-3 border">
                                <div className="font-semibold">{innings.extras_wides}</div>
                                <div className="text-gray-600">Wides</div>
                              </div>
                              <div className="bg-white rounded-lg p-3 border">
                                <div className="font-semibold">{innings.extras_no_balls}</div>
                                <div className="text-gray-600">No Balls</div>
                              </div>
                              <div className="bg-white rounded-lg p-3 border">
                                <div className="font-semibold">{innings.extras_byes}</div>
                                <div className="text-gray-600">Byes</div>
                              </div>
                              <div className="bg-white rounded-lg p-3 border">
                                <div className="font-semibold">{innings.extras_leg_byes}</div>
                                <div className="text-gray-600">Leg Byes</div>
                              </div>
                              <div className="bg-white rounded-lg p-3 border">
                                <div className="font-semibold">{innings.extras_penalty}</div>
                                <div className="text-gray-600">Penalty</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })
                ) : (
                  <div className="text-center text-gray-500 py-8">No innings data available</div>
                )}
              </div>
            )}

            {activeTab === "squads" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{match.teamA?.name}</h3>
                    <div className="space-y-3">
                      {teamAPlayers.map((player) => (
                        <div key={player.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {player.player?.jersey_number || player.player?.player_name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{player.player?.player_name}</h4>
                              <div className="flex gap-2 text-xs">
                                {player.is_captain && (
                                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Captain</span>
                                )}
                                {player.is_wicket_keeper && (
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">WK</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{match.teamB?.name}</h3>
                    <div className="space-y-3">
                      {teamBPlayers.map((player) => (
                        <div key={player.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {player.player?.jersey_number || player.player?.player_name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{player.player?.player_name}</h4>
                              <div className="flex gap-2 text-xs">
                                {player.is_captain && (
                                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Captain</span>
                                )}
                                {player.is_wicket_keeper && (
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">WK</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}